import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingType } from "../../marketing.type";

export type Props = {
  /**
   * Where the person stands: still being read, a link that names nobody,
   * still on the list (asked to confirm), or off it.
   */
  state: "loading" | "invalid" | "subscribed" | "unsubscribed";
  /**
   * Which list the link leaves. Announcements are a setting here, so they can
   * be turned back on; the newsletter lives in Mailchimp, which will not let
   * anybody be put back on an audience they left, so that page offers the
   * signup form instead of a button that could not work.
   */
  kind?: "announcements" | "newsletter";
  /** The address the link belongs to */
  email?: string;
  /** Whether a change is being saved */
  isSaving?: boolean;
  /** Set when saving a change failed */
  error?: string;
  onUnsubscribe: () => void;
  onResubscribe: () => void;
};

/**
 * The page an unsubscribe link opens.
 *
 * It asks before it changes anything, because mail scanners open every link
 * in an email and would otherwise unsubscribe people who never clicked. Only
 * the one list stops: email about meetings and about what the person signed
 * up for keeps coming, and the page says so, since "unsubscribe" usually
 * means everything.
 */
export function MarketingUnsubscribe({
  state,
  kind = "announcements",
  email,
  isSaving = false,
  error,
  onUnsubscribe,
  onResubscribe,
}: Props) {
  const isNewsletter = kind === "newsletter";
  const heading = {
    loading: "One moment.",
    invalid: "This link doesn’t work.",
    subscribed: isNewsletter
      ? "Unsubscribe from the newsletter?"
      : "Unsubscribe from announcements?",
    unsubscribed: "You’re unsubscribed.",
  }[state];

  return (
    <div
      className="flex flex-col gap-4 md:gap-8 w-full max-w-[480px] mx-auto"
      data-testid="unsubscribe"
    >
      <Type
        variant="h3"
        as="h2"
        // Trimmed to its capitals, as the intake confirmations are.
        className="text-[36px] leading-[45px] tracking-[-0.72px] md:text-[36px] md:leading-[45px] font-medium [text-box:trim-both_cap_alphabetic]"
      >
        {heading}
      </Type>

      {state === "invalid" && (
        <Type variant="body1" className={`${marketingType.body} max-w-none`}>
          It may have been cut short when it was copied. Use the link at the
          bottom of the most recent email, or{" "}
          <a
            className="underline decoration-[1.5px] underline-offset-[5px] hover:text-brand-700"
            href="/contact"
          >
            write to us
          </a>{" "}
          and we&rsquo;ll take care of it.
        </Type>
      )}

      {state === "subscribed" && (
        <>
          <Type variant="body1" className={`${marketingType.body} max-w-none`}>
            {isNewsletter ? (
              <>
                We&rsquo;ll stop sending the newsletter to {email}. Email about
                your meetings and anything else you&rsquo;ve signed up for will
                keep coming.
              </>
            ) : (
              <>
                We&rsquo;ll stop sending announcements to {email}. Email about
                your meetings and anything you&rsquo;ve signed up for will keep
                coming.
              </>
            )}
          </Type>
          <div>
            <Button onClick={onUnsubscribe} isLoading={isSaving} disabled={isSaving}>
              Unsubscribe
            </Button>
          </div>
        </>
      )}

      {state === "unsubscribed" && (
        <>
          <Type variant="body1" className={`${marketingType.body} max-w-none`}>
            {isNewsletter ? (
              <>
                We won&rsquo;t send the newsletter to {email} again. Email about
                your meetings and anything else you&rsquo;ve signed up for will
                keep coming.
              </>
            ) : (
              <>
                We won&rsquo;t send announcements to {email} again. Email about
                your meetings and anything you&rsquo;ve signed up for will keep
                coming.
              </>
            )}
          </Type>
          <div>
            {isNewsletter ? (
              // Mailchimp holds the audience and will not let anybody be put
              // back on one they left, so the way back is signing up again.
              <Type variant="body1" className={`${marketingType.body} max-w-none`}>
                Changed your mind? You can{" "}
                <a
                  className="underline decoration-[1.5px] underline-offset-[5px] hover:text-brand-700"
                  href="/#newsletter"
                >
                  sign up again
                </a>
                .
              </Type>
            ) : (
              <Button
                variant="link"
                onClick={onResubscribe}
                isLoading={isSaving}
                disabled={isSaving}
              >
                Changed your mind? Resubscribe
              </Button>
            )}
          </div>
        </>
      )}

      {error && (
        <Type variant="body2" className="text-error" role="alert">
          {error}
        </Type>
      )}
    </div>
  );
}

export default MarketingUnsubscribe;
